import React, { useRef, useState, useEffect } from 'react';
import { Box, useColorMode } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdOutlineNavigateBefore, MdOutlineNavigateNext } from 'react-icons/md';
import { MdSelectAll } from 'react-icons/md';
import { MdCallReceived } from 'react-icons/md';
import { MdOutbound } from 'react-icons/md';
import { MdCallMade } from 'react-icons/md';
import { MdPointOfSale } from 'react-icons/md';
import { MdAssignmentReturn } from 'react-icons/md';

export default function ReceiveTabs({ handleReceiveClick }) {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   const params = useParams();
   const tabListRef = useRef(null);
   const navigate = useNavigate();

   const tabs = [
      {
         label: 'All',
         value: 'screen',
         path: 'inventory/receive/all',
         icon: <MdSelectAll size={20} />,
      },
      {
         label: 'Received Only',
         value: '',
         path: 'inventory/receive',
         icon: <MdCallReceived size={20} />,
      },
      {
         label: 'Issued to Vendor',
         value: 'warehouse',
         path: 'inventory/receive/issue-to-vendor',
         icon: <MdOutbound size={20} />,
      },
      {
         label: 'Received from Vendor',
         value: 'unit',
         path: 'inventory/receive/receive-from-vendor',
         icon: <MdCallMade size={20} />,
      },
      {
         label: 'Sold',
         value: 'item-group',
         path: 'inventory/receive/sold',
         icon: <MdPointOfSale size={20} />,
      },
      {
         label: 'Receive Return',
         value: 'receive-return',
         path: 'inventory/receive/receivereturn',
         icon: <MdAssignmentReturn size={20} />,
      },
   ];

   // Safe handler function
   const safeHandleReceiveClick = (tabValue) => {
      if (typeof handleReceiveClick === 'function') {
         handleReceiveClick(tabValue);
      } else {
         // Fallback navigation if handleReceiveClick is not provided
         const tab = tabs.find((t) => t.value === tabValue);
         if (tab) {
            navigate(`/admin/${tab.path}`);
         }
      }
   };

   const getCurrentTabIndex = () => {
      for (let i = 0; i < tabs.length; i++) {
         if (params['*'] === tabs[i].path) {
            return i;
         }
      }
      return 0;
   };

   const [activeTabIndex, setActiveTabIndex] = useState(getCurrentTabIndex());

   useEffect(() => {
      setActiveTabIndex(getCurrentTabIndex());
   }, [params['*']]);

   const prevTab = activeTabIndex > 0 ? tabs[activeTabIndex - 1] : null;
   const currentTab = tabs[activeTabIndex];
   const nextTab = activeTabIndex < tabs.length - 1 ? tabs[activeTabIndex + 1] : null;

   const getVisibleTabs = (count) => {
      const tabsOnEachSide = Math.floor(count / 2);
      let startIdx = Math.max(0, activeTabIndex - tabsOnEachSide);
      let endIdx = startIdx + count - 1;
      if (endIdx >= tabs.length) {
         endIdx = tabs.length - 1;
         startIdx = Math.max(0, endIdx - count + 1);
      }
      return tabs.slice(startIdx, endIdx + 1);
   };

   const NavButton = ({ disabled, onClick, children }) => (
      <button
         onClick={onClick}
         disabled={disabled}
         className={`p-2 rounded-full transition-all duration-300 ${
            disabled
               ? 'opacity-30 cursor-not-allowed'
               : `cursor-pointer hover:scale-110 ${
                    isDark
                       ? 'hover:bg-slate-800 text-slate-300 hover:text-white'
                       : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                 }`
         }`}>
         {children}
      </button>
   );

   const TabButton = ({ tab, isActive, onClick }) => (
      <button
         onClick={() => onClick(tab.value)}
         className={`flex items-center justify-center gap-2 px-6 py-2 rounded-xl font-semibold text-sm transition-all duration-300 ${
            isActive
               ? `${
                    isDark
                       ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white shadow-lg ring-2 ring-purple-400/50'
                       : 'bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 text-white shadow-lg ring-2 ring-emerald-300/50'
                 } scale-110`
               : `${
                    isDark
                       ? 'text-gray-400 hover:text-white hover:bg-slate-800/70'
                       : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/70'
                 } hover:scale-105`
         }`}>
         <div className={`flex items-center justify-center transition-transform ${isActive ? 'scale-125' : ''}`}>
            {tab.icon}
         </div>
         <span className="whitespace-nowrap">{tab.label}</span>
      </button>
   );

   return (
      <Box pt="">
         <div
            className={`mb-3 rounded-2xl p-2.5 px-4 overflow-hidden ${
               isDark
                  ? 'bg-slate-900/60 backdrop-blur-lg border border-slate-800/80'
                  : 'bg-white border border-gray-200 shadow-xl shadow-gray-100/40'
            }`}>
            {/* Large screens - all tabs */}
            <div className="xl:flex items-center gap-3" ref={tabListRef}>
               {tabs.map((tab) => (
                  <TabButton
                     key={tab.value}
                     tab={tab}
                     isActive={params['*'] === tab.path}
                     onClick={safeHandleReceiveClick}
                  />
               ))}
            </div>

            {/* Desktop - 7 tabs */}
            <div className="hidden lg:flex xl:hidden items-center justify-between gap-2">
               <NavButton
                  disabled={activeTabIndex === 0}
                  onClick={() => safeHandleReceiveClick(tabs[activeTabIndex - 1].value)}>
                  <MdOutlineNavigateBefore size={24} />
               </NavButton>
               <div className="flex items-center gap-1 flex-1 justify-center">
                  {getVisibleTabs(7).map((tab) => (
                     <TabButton
                        key={tab.value}
                        tab={tab}
                        isActive={tab.value === currentTab.value}
                        onClick={safeHandleReceiveClick}
                     />
                  ))}
               </div>
               <NavButton
                  disabled={activeTabIndex === tabs.length - 1}
                  onClick={() => safeHandleReceiveClick(tabs[activeTabIndex + 1].value)}>
                  <MdOutlineNavigateNext size={24} />
               </NavButton>
            </div>

            {/* Tablet - 5 tabs */}
            <div className="hidden md:flex lg:hidden items-center justify-between gap-2">
               <NavButton
                  disabled={activeTabIndex === 0}
                  onClick={() => safeHandleReceiveClick(tabs[activeTabIndex - 1].value)}>
                  <MdOutlineNavigateBefore size={24} />
               </NavButton>
               <div className="flex items-center gap-1 flex-1 justify-center">
                  {getVisibleTabs(5).map((tab) => (
                     <TabButton
                        key={tab.value}
                        tab={tab}
                        isActive={tab.value === currentTab.value}
                        onClick={safeHandleReceiveClick}
                     />
                  ))}
               </div>
               <NavButton
                  disabled={activeTabIndex === tabs.length - 1}
                  onClick={() => safeHandleReceiveClick(tabs[activeTabIndex + 1].value)}>
                  <MdOutlineNavigateNext size={24} />
               </NavButton>
            </div>

            {/* Mobile - single tab + prev/next */}
            <div className="flex md:hidden items-center justify-between gap-2">
               {prevTab ? (
                  <NavButton onClick={() => safeHandleReceiveClick(prevTab.value)}>
                     <MdOutlineNavigateBefore size={22} />
                  </NavButton>
               ) : (
                  <div className="w-8" />
               )}
               <div
                  className={`flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm shadow-xl ${
                     isDark
                        ? 'bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white ring-2 ring-purple-400/50'
                        : 'bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500 text-white ring-2 ring-emerald-300/50'
                  }`}>
                  {currentTab.icon}
                  <span className="truncate">{currentTab.label}</span>
               </div>
               {nextTab ? (
                  <NavButton onClick={() => safeHandleReceiveClick(nextTab.value)}>
                     <MdOutlineNavigateNext size={22} />
                  </NavButton>
               ) : (
                  <div className="w-8" />
               )}
            </div>
         </div>
      </Box>
   );
}
